import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, query, where, orderBy, onSnapshot, updateDoc, doc, arrayUnion, arrayRemove, serverTimestamp } from 'firebase/firestore';
import { db, useFirebaseAuthentication } from 'lib/firebase';
import { Helmet } from 'react-helmet-async';
import SiteWrapper from '../../home-pages/SiteWrapper';
import QuestionReactions from '../../../components/QuestionReactions';

const MomStoriesQueue = () => {
  const navigate = useNavigate();
  const user = useFirebaseAuthentication();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newQuestion, setNewQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    // Query for suggested questions
    const q = query(
      collection(db, 'weekly_questions'),
      where('status', '==', 'suggested'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSuggestions(data);
      setLoading(false);
    }, (error) => {
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSuggest = async () => {
    if (!newQuestion.trim() || !user) return;

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'weekly_questions'), {
        questionText: newQuestion.trim(),
        status: 'suggested',
        votes: 1, // Auto-vote for own suggestion
        voters: [user.uid],
        suggestedBy: user.uid,
        suggestedByName: user.displayName || 'Family Member',
        createdAt: serverTimestamp(),
      });
      
      setNewQuestion('');
      setSnackbar({
        open: true,
        message: 'Question suggested successfully!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to suggest question',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SiteWrapper>
      <Helmet>
        <title>Suggest Questions - Mom Stories</title>
      </Helmet>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pb: 6, mt: '-64px', pt: '64px' }}>
        <Container maxWidth="md" sx={{ pt: 2 }}>
          {/* Header */}
          <Box sx={{ mb: 6, textAlign: 'center', position: 'relative' }}>
            {/* Navigation Buttons - Responsive Layout */}
            <Box 
              sx={{ 
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: { xs: 2, sm: 0 },
                position: { xs: 'relative', sm: 'absolute' },
                top: { sm: 0 },
                left: { sm: 0 },
                right: { sm: 0 },
                width: '100%',
                zIndex: 1
              }}
            >
              <Button 
                startIcon={<ArrowBackIcon />} 
                onClick={() => navigate('/mom-stories')}
                sx={{ 
                  color: '#667eea',
                  '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.05)' }
                }}
              >
                Back
              </Button>
              {user && user.email === 'rdymock@gmail.com' && (
                <Button 
                  startIcon={<AdminPanelSettings />} 
                  onClick={() => navigate('/mom-stories/admin')}
                  sx={{ 
                    color: '#64748b',
                    '&:hover': { bgcolor: 'rgba(100, 116, 139, 0.1)' }
                  }}
                >
                  Admin
                </Button>
              )}
            </Box>

            {/* Title - Add top margin on desktop to account for absolute buttons */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                mt: { xs: 0, sm: 6 }
              }}
            >
              Future Questions
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Help decide what Mom answers next!
            </Typography>
          </Box>

          {/* Suggestion Form */}
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Suggest a New Question
            </Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2 
              }}
            >
              <TextField
                fullWidth
                placeholder="e.g., What was your favorite vacation as a child?"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                variant="outlined"
                size="small"
              />
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                onClick={handleSuggest}
                disabled={!newQuestion.trim() || submitting || !user}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  minWidth: { xs: '100%', sm: 120 },
                  color: '#fff !important',
                  fontWeight: 'bold !important',
                  boxShadow: '0 4px 6px rgba(102, 126, 234, 0.25)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a3f8f 100%)',
                    boxShadow: '0 6px 8px rgba(102, 126, 234, 0.35)',
                  }
                }}
              >
                Suggest
              </Button>
            </Box>
            {!user && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                You must be signed in to suggest questions.
              </Typography>
            )}
          </Paper>

          {/* Questions List */}
          <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : suggestions.length === 0 ? (
              <Box textAlign="center" p={4}>
                <Typography color="text.secondary">
                  No suggestions yet. Be the first to suggest one!
                </Typography>
              </Box>
            ) : (
              <List sx={{ bgcolor: 'background.paper' }}>
                {suggestions.map((question, index) => {
                  return (
                    <React.Fragment key={question.id}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" fontWeight={500}>
                              {question.questionText}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                Suggested by {question.suggestedByName}
                              </Typography>
                              <QuestionReactions 
                                question={question} 
                                user={user}
                                onError={(msg) => setSnackbar({ open: true, message: msg, severity: 'error' })}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Paper>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </SiteWrapper>
  );
};

export default MomStoriesQueue;
